import { useEffect, useState } from 'react';
import { CheckCircle2, Calendar, Users, Zap, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import VideoModal, { DEMO_VIDEO_URL } from '../components/VideoModal';

export default function Demo() {
  useEffect(() => window.scrollTo(0, 0), []);
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', teamSize: '1-10' });
  const [submitted, setSubmitted] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast({ title: 'Missing info', description: 'Name, email and phone are required.' });
      return;
    }
    const stored = JSON.parse(localStorage.getItem('actiwa_demos') || '[]');
    stored.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem('actiwa_demos', JSON.stringify(stored));
    setSubmitted(true);
    toast({ title: 'Demo booked!', description: 'Our team will reach out shortly.' });
  };

  return (
    <section className="section-fade min-h-[80vh] py-12">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        {/* Video preview banner */}
        <div className="mb-10">
          <button
            onClick={() => setVideoOpen(true)}
            className="group relative w-full rounded-3xl overflow-hidden bg-gradient-to-br from-brand-purple to-brand-purple-dark p-1 shadow-xl shadow-brand-purple/20"
          >
            <div className="relative rounded-[22px] overflow-hidden bg-slate-900 aspect-[16/7] flex items-center justify-center">
              <video
                src={DEMO_VIDEO_URL}
                muted
                playsInline
                preload="metadata"
                className="absolute inset-0 w-full h-full object-cover opacity-70"
              />
              <div className="relative flex flex-col items-center text-white">
                <span className="w-20 h-20 rounded-full bg-white/95 grid place-items-center shadow-2xl group-hover:scale-110 transition-transform">
                  <Play size={32} className="text-brand-purple fill-brand-purple ml-1" />
                </span>
                <p className="mt-4 text-sm md:text-base font-semibold uppercase tracking-widest text-white/90">
                  Watch ACTIWA Demo
                </p>
              </div>
            </div>
          </button>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
        <div>
          <p className="chip mb-4">Book a Free Demo</p>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
            See how ACTIWA can transform your business
          </h1>
          <p className="mt-5 text-slate-600 text-lg">
            Get a personalized 30-minute walkthrough of the platform. Our expert will help you set up your first WhatsApp campaign.
          </p>

          <div className="mt-8 space-y-4">
            {[
              { Icon: Calendar, t: '30-min personalized demo', d: 'One-on-one with a WhatsApp expert' },
              { Icon: Zap, t: 'See ACTIWA in action', d: 'Live walkthrough of AI, inbox and broadcasts' },
              { Icon: Users, t: 'Get pricing for your team', d: 'Custom quotes based on your size' },
            ].map(({ Icon, t, d }) => (
              <div key={t} className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 grid place-items-center shrink-0">
                  <Icon size={18} />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{t}</p>
                  <p className="text-sm text-slate-600">{d}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
            {['No credit card required', '7-day free trial', 'Cancel anytime'].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5">
                <CheckCircle2 size={16} className="text-emerald-500" /> {t}
              </span>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-3xl p-7 shadow-lg shadow-slate-200/50">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 grid place-items-center mx-auto">
                <CheckCircle2 size={32} />
              </div>
              <h3 className="mt-4 text-2xl font-extrabold text-slate-900">You're all set!</h3>
              <p className="mt-2 text-slate-600">Our team will contact you within 24 hours to schedule your demo.</p>
              <Button onClick={() => setSubmitted(false)} variant="outline" className="mt-6 rounded-full">
                Book another
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <h3 className="text-xl font-bold text-slate-900">Book your free demo</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-slate-800">Full name *</label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11 rounded-lg" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-800">Work email *</label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11 rounded-lg" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-slate-800">Phone *</label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-11 rounded-lg" placeholder="+91" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-800">Company</label>
                  <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1.5 h-11 rounded-lg" />
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-800">Team size</label>
                <select
                  value={form.teamSize}
                  onChange={(e) => setForm({ ...form, teamSize: e.target.value })}
                  className="mt-1.5 w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm"
                >
                  <option>1-10</option>
                  <option>11-50</option>
                  <option>51-200</option>
                  <option>200+</option>
                </select>
              </div>
              <Button type="submit" className="w-full h-12 bg-brand-purple hover:bg-brand-purple-dark text-white rounded-full font-semibold">
                Book my free demo
              </Button>
              <p className="text-xs text-center text-slate-500">By booking, you agree to our Terms & Privacy Policy.</p>
            </form>
          )}
        </div>
        </div>
      </div>
      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} />
    </section>
  );
}
